
import React, { useContext, useEffect, useState } from 'react';
import { Course, UserRole } from '../../types/lms.types';
import { getAxiosTokenInstance } from '../../../utils/axiosInstance';
import { NecttosContext } from '../../../context/NecttosContext';
import { formatDate } from '../../utilities';

interface GradesAnalyticsProps {
  course: Course;
  userRole: UserRole;
  selectedModule: string;
}

const GradesAnalytics: React.FC<GradesAnalyticsProps> = ({ course, userRole, selectedModule }) => {

  const [analytics, setAnalytics] = useState<{id: string, mark: number, maxMark: number, moduleId: string, studentName: string, title: string, submittedAt: any, percentage: number}[]>([])

  
  const { collegeId } = useContext(NecttosContext)
 useEffect(() => {
const loadData = async() => {
  const instance = await getAxiosTokenInstance();
  try{
   const resp = await instance.get('/college/lms/grades/getGrades', {
    params: {
      subjectId: course.subjectId,
      collegeId
    }
   });
   if(resp?.data){
    console.log(resp.data.data)
    setAnalytics(resp.data.data);
   }
  }catch(err) {
   console.log(err)
  }
   
}
loadData();
 },[])
  // Filter grades based on selected module
  const filteredGrades = analytics.filter(grade => 
    selectedModule === 'all' || grade.moduleId === selectedModule
  );

  const getGradePercentage = (score: number, maxScore: number) => {
    return Math.round((score / maxScore) * 100);
  };

  const getGradeBadge = (percentage: number) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    if (percentage >= 90) {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else if (percentage >= 80) {
      return `${baseClasses} bg-blue-100 text-blue-800`;
    } else if (percentage >= 70) {
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    } else {
      return `${baseClasses} bg-red-100 text-red-800`;
    }
  };

  const calculateStats = () => {
    if (filteredGrades.length === 0) return { average: 0, highest: 0, lowest: 0 };
    
    const percentages = filteredGrades.map(grade => getGradePercentage(grade.mark, grade.maxMark));
    const average = Math.round(percentages.reduce((sum, p) => sum + p, 0) / percentages.length);
    const highest = Math.max(...percentages);
    const lowest = Math.min(...percentages);
    
    return { average, highest, lowest };
  };

  const stats = calculateStats();

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="text-xl font-semibold">Grades Analytics</h2>
      </div>

      {/* Stats Overview */}
      {filteredGrades.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.average}%</div>
            <div className="text-sm text-gray-500">Average Score</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-2xl font-bold text-green-600">{stats.highest}%</div>
            <div className="text-sm text-gray-500">Highest Score</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-2xl font-bold text-red-600">{stats.lowest}%</div>
            <div className="text-sm text-gray-500">Lowest Score</div>
          </div>
        </div>
      )}

      {/* Grades Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium">Grade Details</h3>
        </div>
        
        {analytics.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Grade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {analytics.map((grade) => {
                  const percentage = getGradePercentage(grade.mark, grade.maxMark);
                  return (
                    <tr key={grade.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {grade.studentName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {grade.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {grade.mark}/{grade.maxMark}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getGradeBadge(grade.percentage)}>
                          {grade.percentage}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(grade.submittedAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <span className="text-6xl mb-4 block">📊</span>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No grades found
            </h3>
            <p className="text-gray-500">
              No grades available for the selected module.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GradesAnalytics;
